// src/pages/ShopifyInstallHandler.jsx
// Uses Shopify App Bridge Redirect for OAuth flow
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spinner, Page, Layout, Card, Text, Button } from '@shopify/polaris';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

/**
 * This component serves as the frontend entry point for Shopify app installation.
 * It uses App Bridge Redirect to properly break out of the iframe for OAuth.
 */
export default function ShopifyInstallHandler() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('initializing');
  const [error, setError] = useState(null);
  const [oauthUrl, setOauthUrl] = useState(null);

  useEffect(() => {
    const initiateOAuthFlow = async () => {
      // Extract all possible Shopify parameters
      const shop = searchParams.get('shop');
      const host = searchParams.get('host');
      const hmac = searchParams.get('hmac');
      const timestamp = searchParams.get('timestamp');
      const session = searchParams.get('session');
      const locale = searchParams.get('locale');
      const embedded = searchParams.get('embedded');
      
      // Validation
      if (!shop) {
        setError('Missing shop parameter');
        setStatus('error');
        return;
      }

      // Validate shop format
      if (!shop.match(/^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/)) {
        setError('Invalid shop domain format');
        setStatus('error');
        return;
      }
      
      // Clear stale auth/session state before OAuth
      // This prevents "one site per account" conflicts from 
      // leftover tokens/state from previous test sessions
      
      // Clear auth tokens
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('jwtToken');
      localStorage.removeItem('token');
      localStorage.removeItem('jwtToken');
      
      // Clear site-specific state
      sessionStorage.removeItem('site_id');
      sessionStorage.removeItem('site_token');
      sessionStorage.removeItem('shop_domain');
      sessionStorage.removeItem('shopifyShop');
      sessionStorage.removeItem('pendingShopifySite');
      
      // Clear any shop-specific setup flags (pattern: shop_*_setup, shop_*_site_id, etc.)
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('shop_') || key.startsWith('bb_') || key.startsWith('baobrain_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));

      // Build backend OAuth initiation URL with all parameters
      const backendUrl = new URL(`${API}/shopify/install`);
      
      // Add all available parameters
      backendUrl.searchParams.set('shop', shop);
      if (host) backendUrl.searchParams.set('host', host);
      if (hmac) backendUrl.searchParams.set('hmac', hmac);
      if (timestamp) backendUrl.searchParams.set('timestamp', timestamp);
      if (session) backendUrl.searchParams.set('session', session);
      if (locale) backendUrl.searchParams.set('locale', locale);
      if (embedded) backendUrl.searchParams.set('embedded', embedded);

      const fullUrl = backendUrl.toString();

      // Check if we're in an embedded context
      if (host && window.shopify) {
        // Use App Bridge to redirect (works in iframe)
        try {
          // App Bridge 4.x uses window.shopify
          await window.shopify.redirectToRemote({ url: fullUrl });
        } catch (err) {
          console.error('[ShopifyInstallHandler] App Bridge redirect failed:', err);
          // Fall back to showing a button
          setOauthUrl(fullUrl);
          setStatus('needs_click');
        }
      } else if (host) {
        // We're embedded but App Bridge isn't ready - show a button
        setOauthUrl(fullUrl);
        setStatus('needs_click');
      } else {
        // Not embedded - direct redirect is fine
        window.location.href = fullUrl;
      }
    };

    initiateOAuthFlow();
  }, [searchParams]);

  // Handle manual click for OAuth redirect
  const handleOAuthClick = () => {
    if (oauthUrl) {
      // This will work because it's triggered by a user gesture
      window.open(oauthUrl, '_top');
    }
  };

  // Error state
  if (status === 'error') {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Text variant="headingLg" as="h2">
                  Installation Error
                </Text>
                <div style={{ marginTop: '16px' }}>
                  <Text variant="bodyMd" as="p" tone="critical">
                    {error || 'An error occurred during installation'}
                  </Text>
                </div>
                <div style={{ marginTop: '20px' }}>
                  <Text variant="bodyMd" as="p">
                    Please try again or contact support if the problem persists.
                  </Text>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Needs user click to proceed (fallback when App Bridge redirect fails)
  if (status === 'needs_click') {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ 
                padding: '40px', 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px'
              }}>
                <Text variant="headingMd" as="h2">
                  Connect BaoBrain to Your Store
                </Text>
                <Text variant="bodyMd" as="p" tone="subdued">
                  Click the button below to authorize BaoBrain
                </Text>
                <Button primary size="large" onClick={handleOAuthClick}>
                  Continue to Authorization
                </Button>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Loading state
  return (
    <Page>
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ 
              padding: '40px', 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              <Spinner size="large" />
              <Text variant="headingMd" as="h2">
                Connecting to Shopify...
              </Text>
              <Text variant="bodyMd" as="p" tone="subdued">
                Please wait while we set up your BaoBrain integration
              </Text>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}